import {Icon} from '@wandb/weave/components/Icon';
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {useWeaveContext} from '@wandb/weave/context';
import {
  constFunction,
  Node,
  opArtifactCreatedAt,
  opArtifactDescription,
  opArtifactId,
  opArtifactIsPortfolio,
  opArtifactMembershipArtifactVersion,
  opArtifactMemberships,
  opArtifactName,
  opArtifactProject,
  opArtifactType,
  opArtifactTypeName,
  opArtifactVersionCreatedAt,
  opArtifactVersionIsGenerated,
  opArtifactVersionTTLDurationSeconds,
  opDict,
  opEntityName,
  opMap,
  opProjectEntity,
  opProjectName,
  OutputNode,
} from '@wandb/weave/core';
import {useNodeValue as weaveUseNodeValue} from '@wandb/weave/react';
import * as React from 'react';
import {RefObject} from 'react';
import {toast} from 'react-toastify';

import * as Generated from '../../../../generated/graphql';
import {
  addSeconds,
  DateFormat,
  differenceInDays,
  format,
} from '../../../../util/date';
import DOC_URLS from '../../../../util/doc_urls';
import {propagateErrorsContext} from '../../../../util/errors';
import {capitalizeFirstLetter} from '../../../../util/string';
import {
  BANNER_VARIANTS,
  BannerBoldText,
  BannerButton,
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../../Banner';
import PanelArtifactTagList from '../../artifact/PanelArtifactTagList';
import {NotificationsOverviewWrapper} from './NotificationsOverviewWrapper';
import {OverviewItem} from './OverviewItem';
import * as S from './style';
import {TriggersOverview} from './TriggersOverview';

const artifactInputType = 'artifact' as const;
export const PanelArtifactMembershipOverviewCollectionSection: React.FC<
  Panel2.PanelProps<typeof artifactInputType, {}> & {
    readOnly: boolean;
    artifactRegistry?: boolean;
    inputRef: RefObject<HTMLDivElement> | null;
  }
> = props => {
  const {input: artifactNode, readOnly} = props;
  const [updateArtifactCollection] =
    Generated.useUpdateArtifactCollectionMutation({
      context: propagateErrorsContext(),
    });
  const [updateArtifactPortfolio] =
    Generated.useUpdateArtifactPortfolioMutation({
      context: propagateErrorsContext(),
    });
  const {result, loading} = weaveUseNodeValue(
    React.useMemo(() => {
      return getQuery(artifactNode);
    }, [artifactNode])
  );
  // For clearing the cache after updating the description
  const weave = useWeaveContext();

  const onUpdateDescription: React.ComponentProps<
    typeof S.OverviewValueMarkdownEditor
  >['onChange'] = React.useCallback(
    (value: string) => {
      if (result?.artifactIsPortfolio) {
        updateArtifactPortfolio({
          variables: {
            artifactPortfolioID: result?.artifactId,
            description: value,
          },
        }).catch(err => {
          console.error(err);
          toast('Something went wrong while trying to change the description.');
        });
      } else {
        updateArtifactCollection({
          variables: {
            artifactSequenceID: result?.artifactId,
            description: value,
          },
        }).catch(err => {
          console.error(err);
          toast('Something went wrong while trying to change the description.');
        });
      }
      // After updating description, clear the local storage cache
      weave.client.clearCacheForNode(getQuery(artifactNode));
    },
    [
      result,
      updateArtifactCollection,
      updateArtifactPortfolio,
      artifactNode,
      weave.client,
    ]
  );

  const artifactVersionTTLStatus = (aNode: Node<'artifact'>) => {
    const versionResults = opDict({
      collectionMemberships: opMap({
        arr: opArtifactMemberships({
          artifact: aNode,
        }),
        mapFn: constFunction({row: 'artifactMembership'}, ({row}) => {
          return opDict({
            createdAt: opArtifactVersionCreatedAt({
              artifactVersion: opArtifactMembershipArtifactVersion({
                artifactMembership: row,
              }),
            }),
            ttlDurationSeconds: opArtifactVersionTTLDurationSeconds({
              artifactVersion: opArtifactMembershipArtifactVersion({
                artifactMembership: row,
              }),
            }),
            isGenerated: opArtifactVersionIsGenerated({
              artifactVersion: opArtifactMembershipArtifactVersion({
                artifactMembership: row,
              }),
            }),
          } as any) as any;
        }),
      }),
    } as any) as unknown as OutputNode<{
      type: 'typedDict';
      propertyTypes: {
        collectionMemberships: {
          type: 'list';
          objectType: {
            type: 'typedDict';
            propertyTypes: {
              createdAt: 'any';
              ttlDurationSeconds: 'number';
              isGenerated: 'boolean';
            };
          };
        };
      };
    }>;

    let warningType;
    const errorDays = 7;
    const warningDays = 30;
    const versions =
      weaveUseNodeValue(versionResults).result?.collectionMemberships;

    if (versions) {
      for (const v of versions) {
        if (v.ttlDurationSeconds <= 0 || v.isGenerated) {
          continue;
        }
        const expireTimestamp = addSeconds(
          v.createdAt != null ? new Date(v.createdAt) : new Date(),
          v.ttlDurationSeconds
        );
        const ttlDurationLeft = differenceInDays(expireTimestamp, new Date());
        if (ttlDurationLeft <= errorDays) {
          warningType = BANNER_VARIANTS.ERROR;
          break;
        } else if (ttlDurationLeft <= warningDays) {
          warningType = BANNER_VARIANTS.WARNING;
        }
      }
    }
    return warningType;
  };

  const ttlStatus = artifactVersionTTLStatus(artifactNode);

  return (
    <div>
      <S.OverviewPanelSection>
        <S.OverviewPanelSectionHeader>
          <S.OverviewPanelSectionHeaderTitle
            data-test={`${result?.artifactTypeName}-card`}>
            {result?.artifactIsPortfolio
              ? `${capitalizeFirstLetter(result?.artifactTypeName)} card`
              : 'Artifact overview'}
          </S.OverviewPanelSectionHeaderTitle>
        </S.OverviewPanelSectionHeader>
        <S.OverviewPanelSectionBody>
          <S.OverviewPanelSectionBodyRow>
            <S.OverviewPanelSectionBodyColumn>
              {props.artifactRegistry && (
                <>
                  <OverviewItem
                    label={'Tags'}
                    value={
                      <PanelArtifactTagList
                        {...Panel2.dummyProps}
                        input={artifactNode}
                        showButton={true}
                        wrap={true}
                        showColor={true}
                        tagSize={'large'}
                      />
                    }
                  />
                  <OverviewItem
                    label={'Full name'}
                    value={`${result?.entityName}/${result?.projectName}/${result?.artifactName}`}
                  />
                </>
              )}
              <OverviewItem label={'Type'} value={result?.artifactTypeName} />
              <OverviewItem
                label="Created At"
                value={format(
                  result?.artifactCreatedAt != null
                    ? new Date(result?.artifactCreatedAt)
                    : new Date(),
                  DateFormat.MONTH_DAYTH_YEAR,
                  'UTC'
                )}
              />
              {props.artifactRegistry && (
                <>
                  <OverviewItem
                    label="Automations"
                    value={
                      <TriggersOverview
                        {...Panel2.dummyProps}
                        input={artifactNode}
                        inputRef={props.inputRef}
                      />
                    }
                  />
                  <OverviewItem
                    label="Slack notifications"
                    value={
                      <NotificationsOverviewWrapper
                        {...Panel2.dummyProps}
                        input={artifactNode}
                      />
                    }
                  />
                </>
              )}
            </S.OverviewPanelSectionBodyColumn>
          </S.OverviewPanelSectionBodyRow>
          <S.OverviewPanelSectionBodyRow>
            <OverviewItem
              label={'Description'}
              value={
                loading ? (
                  <></>
                ) : (
                  <S.OverviewValueMarkdownEditor
                    readOnly={readOnly}
                    saveText={false}
                    placeholder={'Add a markdown description...'}
                    serverText={result?.artifactDescription}
                    onChange={onUpdateDescription}
                  />
                )
              }
            />
          </S.OverviewPanelSectionBodyRow>
        </S.OverviewPanelSectionBody>
      </S.OverviewPanelSection>
      {ttlStatus != null && (
        <S.BannerWithMargin variant={ttlStatus}>
          <BannerFlexWrapper>
            <Icon
              name={ttlStatus === BANNER_VARIANTS.INFO ? 'info' : 'warning-alt'}
            />
            <BannerTextWrapper>
              {<BannerBoldText>Versions expiring soon.</BannerBoldText>} One or
              more artifact versions are close to expiration.
            </BannerTextWrapper>
          </BannerFlexWrapper>

          <BannerButton
            variant={ttlStatus}
            text="Learn more"
            link={DOC_URLS.artifactTTL}
            eventData={{
              buttonInfo: 'Artifact TTL Collection Banner Button Clicked',
              entityName: result?.entityName,
              projectName: result?.projectName,
              artifactID: result?.artifactId,
              artifactTTLDuration: result?.ttlDurationSeconds,
            }}
          />
        </S.BannerWithMargin>
      )}
    </div>
  );
};

// The query is used by both fetching the data and invalidating the cache.
// Hash of the query is used as the cache key. e.g. wv_123456, where 123456 is hash of the query.
function getQuery(artifactNode: Node<'artifact'>) {
  const projectNode = opArtifactProject({
    artifact: artifactNode,
  });
  return opDict({
    artifactId: opArtifactId({
      artifact: artifactNode,
    }),
    artifactName: opArtifactName({
      artifact: artifactNode,
    }),
    artifactDescription: opArtifactDescription({
      artifact: artifactNode,
    }),
    artifactCreatedAt: opArtifactCreatedAt({
      artifact: artifactNode,
    }),
    artifactTypeName: opArtifactTypeName({
      artifactType: opArtifactType({artifact: artifactNode}),
    }),
    artifactIsPortfolio: opArtifactIsPortfolio({
      artifact: artifactNode,
    }),
    entityName: opEntityName({
      entity: opProjectEntity({
        project: projectNode,
      }),
    }),
    projectName: opProjectName({
      project: projectNode,
    }),
  } as any);
}
